import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import {
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
} from "@mui/icons-material";
import { CustomButton } from "../../components/common/customButton";
import { CustomIconButton } from "../../components/common/customIconButton";
import { CustomSingleSelect } from "../../components/common/inputFields/customSingleSelect";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import {
  listExtractions,
  getExtractionStatus,
  deleteExtraction,
} from "../../services/extractionApi";
import type {
  Extraction,
  ExtractionStatus,
  ImageExtractionStatus,
} from "../../types/extraction";
import "../../assets/css/Text-General.css";
import searchIcon from "../../assets/svg/search.svg";
import binIcon from "../../assets/svg/bin.svg";
import { parseInstantUtc } from "../../utils/formatDate";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "../../components/common/errorDialog";

// Relative time from an API ISO string or Date (UTC-safe for naive ISO).
const formatDistanceToNow = (
  input: string | Date,
  options?: { addSuffix?: boolean },
): string => {
  const date =
    typeof input === "string" ? parseInstantUtc(input) : input;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  let result = "";
  if (diffDays > 0) {
    result = `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  } else if (diffHours > 0) {
    result = `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  } else if (diffMinutes > 0) {
    result = `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  } else {
    result = "just now";
  }

  return options?.addSuffix ? result : result.replace(" ago", "");
};

const POLL_INTERVAL = 20000; // 20 seconds
const MAX_POLLS = 200; // 200 polls x 20s = ~66 minutes max per extraction
const AUTO_REFRESH_INTERVAL = 20000; // 20 seconds

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return "N/A";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

const isExtractionApplied = (extraction: Extraction): boolean =>
  extraction.used_for_deal ?? extraction.deal_id != null;

const canDeleteExtraction = (extraction: Extraction): boolean =>
  extraction.status === "completed" ||
  extraction.status === "failed" ||
  extraction.status === "cancelled";

const getStatusIcon = (status: ExtractionStatus) => {
  switch (status) {
    case "completed":
      return <CheckCircle sx={{ fontSize: 16, mr: 0.5 }} />;
    case "failed":
      return <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    case "processing":
      return <CircularProgress size={16} sx={{ mr: 0.5 }} />;
    case "pending":
      return <HourglassEmpty sx={{ fontSize: 16, mr: 0.5 }} />;
    default:
      return null;
  }
};

const getImageStatusChipSx = (status: ImageExtractionStatus) => {
  switch (status) {
    case "completed":
      return { backgroundColor: "#e6f6ef", color: "#0f6b45" };
    case "processing":
      return { backgroundColor: "#e7f1ff", color: "#0d4f8b" };
    case "pending":
      return { backgroundColor: "#fff5e6", color: "#8a5200" };
    case "failed":
      return { backgroundColor: "#feeceb", color: "#9a2218" };
    case "skipped":
      return { backgroundColor: "#eef1f4", color: "#4a5565" };
    default:
      return { backgroundColor: "#eef1f4", color: "#4a5565" };
  }
};

const getStatusChipSx = (status: ExtractionStatus) => {
  switch (status) {
    case "completed":
      return {
        backgroundColor: "#e6f6ef",
        color: "#0f6b45",
      };
    case "processing":
      return {
        backgroundColor: "#e7f1ff",
        color: "#0d4f8b",
      };
    case "pending":
      return {
        backgroundColor: "#fff5e6",
        color: "#8a5200",
      };
    case "failed":
      return {
        backgroundColor: "#feeceb",
        color: "#9a2218",
      };
    case "cancelled":
      return {
        backgroundColor: "#eef1f4",
        color: "#4a5565",
      };
    default:
      return {
        backgroundColor: "#eef1f4",
        color: "#4a5565",
      };
  }
};

const DUPLICATE_REUSE_MESSAGE =
  "This email was already extracted — showing existing result.";

const AIExtractions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const isDuplicateReuse = searchParams.get("duplicate") === "true";
  const duplicateMessage =
    (location.state as { duplicateMessage?: string } | null)?.duplicateMessage ??
    DUPLICATE_REUSE_MESSAGE;
  const autoOpenedDuplicateRef = useRef(false);

  const [extractions, setExtractions] = useState<Extraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ExtractionStatus | "all">(
    "all",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [showApplied, setShowApplied] = useState(true);
  // Extraction ids currently being polled. `interval` is null while the first
  // (immediate) poll is in flight and the interval has not been created yet.
  const pollingRefs = useRef<
    Map<number, { interval: ReturnType<typeof setInterval> | null }>
  >(new Map());
  const isMountedRef = useRef(false);
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  const fetchExtractions = useCallback(async () => {
    try {
      const response = await listExtractions({
        status: statusFilter !== "all" ? statusFilter : undefined,
        limit: 100,
      });

      if (response && response.success && response.data) {
        setExtractions(response.data.extractions || []);
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      console.error("Error fetching extractions:", error);
      showError(
        undefined,
        error instanceof Error ? error.message : "Failed to fetch extractions",
        "Error",
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]); // Only depend on statusFilter, error handlers are stable

  const stopPolling = useCallback((extractionId: number) => {
    const entry = pollingRefs.current.get(extractionId);
    if (entry?.interval) {
      clearInterval(entry.interval);
    }
    pollingRefs.current.delete(extractionId);
  }, []);

  const pollExtractionStatus = useCallback(
    async (extractionId: number) => {
      let pollCount = 0;

      const poll = async () => {
        if (pollCount >= MAX_POLLS) {
          // Stop polling after max attempts
          stopPolling(extractionId);
          return;
        }

        try {
          const response = await getExtractionStatus(extractionId);

          if (!isMountedRef.current) return;

          if (response && response.success && response.data) {
            const status = response.data;
            // Update the extraction in the list
            setExtractions((prev) =>
              prev.map((ext) =>
                ext.id === extractionId ? { ...ext, ...status } : ext,
              ),
            );

            // Stop polling if completed or failed
            if (
              status.status === "completed" ||
              status.status === "failed" ||
              status.status === "cancelled"
            ) {
              stopPolling(extractionId);
            }
          }
        } catch (error) {
          console.error(`Error polling extraction ${extractionId}:`, error);
        }

        pollCount++;
      };

      // Mark as polling before the first request so the setup effect does not
      // start a second poller for the same id while it is in flight.
      const entry: { interval: ReturnType<typeof setInterval> | null } = {
        interval: null,
      };
      pollingRefs.current.set(extractionId, entry);

      // Poll immediately, then set up interval
      await poll();

      // Don't create an interval if the page unmounted during the first poll,
      // or if this poller was stopped (terminal status / max polls / cleared).
      if (
        !isMountedRef.current ||
        pollingRefs.current.get(extractionId) !== entry
      ) {
        return;
      }
      entry.interval = setInterval(() => void poll(), POLL_INTERVAL);
    },
    [stopPolling],
  );

  // Track mount state and clear all polling intervals on unmount only.
  useEffect(() => {
    isMountedRef.current = true;
    const intervals = pollingRefs.current;
    return () => {
      isMountedRef.current = false;
      intervals.forEach((entry) => {
        if (entry.interval) clearInterval(entry.interval);
      });
      intervals.clear();
    };
  }, []);

  // Initial fetch and when statusFilter changes
  useEffect(() => {
    fetchExtractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]); // Only fetch when statusFilter changes, not when fetchExtractions changes

  // Memoize whether there are active extractions to prevent infinite loops
  const hasActiveExtractions = useMemo(() => {
    return extractions.some(
      (ext) => ext.status === "pending" || ext.status === "processing",
    );
  }, [extractions]);

  // Set up auto-refresh for pending/processing extractions
  useEffect(() => {
    // Clear any existing interval
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }

    if (!hasActiveExtractions) return;

    // Use a stable reference to fetchExtractions
    const fetchFn = async () => {
      try {
        const response = await listExtractions({
          status: statusFilter !== "all" ? statusFilter : undefined,
          limit: 100,
        });

        if (response && response.success && response.data) {
          setExtractions(response.data.extractions || []);
        } else {
          showErrorFromResponse(response);
        }
      } catch (error) {
        console.error("Error fetching extractions:", error);
        showError(
          undefined,
          error instanceof Error
            ? error.message
            : "Failed to fetch extractions",
          "Error",
        );
      }
    };

    autoRefreshIntervalRef.current = setInterval(
      fetchFn,
      AUTO_REFRESH_INTERVAL,
    );

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveExtractions, statusFilter]); // Only depend on hasActiveExtractions and statusFilter

  // Start polling for pending/processing extractions that aren't polled yet.
  // Intervals are intentionally not cleared here: `extractions` changes on
  // every poll tick, and clearing would stop polling after one tick. Pollers
  // stop themselves on terminal status / max polls, and the unmount effect
  // above clears whatever is left.
  useEffect(() => {
    extractions.forEach((extraction) => {
      if (
        (extraction.status === "pending" ||
          extraction.status === "processing") &&
        !pollingRefs.current.has(extraction.id)
      ) {
        void pollExtractionStatus(extraction.id);
      }
    });
  }, [extractions, pollExtractionStatus]);

  // Scroll to highlighted extraction
  useEffect(() => {
    if (highlightId) {
      const element = document.getElementById(`extraction-${highlightId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.style.backgroundColor = "#fff3cd";
          setTimeout(() => {
            element.style.backgroundColor = "";
          }, 3000);
        }, 500);
      }
    }
  }, [highlightId, extractions]);

  // Duplicate upload: open existing result when already completed (no new job to poll).
  useEffect(() => {
    if (
      !isDuplicateReuse ||
      !highlightId ||
      autoOpenedDuplicateRef.current ||
      loading
    ) {
      return;
    }

    const extraction = extractions.find(
      (ext) => String(ext.id) === highlightId,
    );
    if (!extraction || extraction.status !== "completed") return;

    autoOpenedDuplicateRef.current = true;
    navigate(`/ai-extractions/${extraction.id}`);
  }, [
    extractions,
    highlightId,
    isDuplicateReuse,
    loading,
    navigate,
  ]);

  const handleDelete = async (extraction: Extraction) => {
    if (
      !window.confirm(
        `Are you sure you want to delete extraction "${extraction.file_name}"?`,
      )
    ) {
      return;
    }

    try {
      const response = await deleteExtraction(extraction.id);

      if (response && response.success) {
        // Remove from list
        setExtractions((prev) =>
          prev.filter((ext) => ext.id !== extraction.id),
        );
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      console.error("Error deleting extraction:", error);
      showError(
        undefined,
        error instanceof Error ? error.message : "Failed to delete extraction",
        "Error",
      );
    }
  };

  const filteredExtractions = useMemo(() => {
    return extractions
      .filter((extraction) => {
        if (!showApplied && isExtractionApplied(extraction)) {
          return false;
        }

        if (searchQuery) {
          return extraction.file_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => {
        const appliedOrder =
          Number(isExtractionApplied(a)) - Number(isExtractionApplied(b));
        if (appliedOrder !== 0) return appliedOrder;

        const aTime = parseInstantUtc(a.created_at).getTime();
        const bTime = parseInstantUtc(b.created_at).getTime();
        return bTime - aTime;
      });
  }, [extractions, showApplied, searchQuery]);

  return (
    <Box sx={{ p: "12px" }}>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />

      {isDuplicateReuse && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {duplicateMessage}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          className="text-general-page-header"
          sx={{ mb: "0 !important", pb: 0 }}
        >
          AI Extractions
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, mt: 1 }}
          className="text-general-page-text"
        >
          View and manage your document extractions
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <Box sx={{ minWidth: 200 }}>
          <CustomSingleSelect
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ExtractionStatus | "all")
            }
            options={[
              { id: "all", label: "All" },
              { id: "pending", label: "Pending" },
              { id: "processing", label: "Processing" },
              { id: "completed", label: "Completed" },
              { id: "failed", label: "Failed" },
            ]}
            placeholder="Status"
            labelKey="label"
            isStringArray={false}
          />
        </Box>

        <Box sx={{ flexGrow: 1, maxWidth: 400 }}>
          <CustomTextInput
            label="Search by filename"
            value={searchQuery}
            onChange={(value) => setSearchQuery(String(value))}
            type="text"
          />
        </Box>

        <CustomButton
          text={showApplied ? "Hide Applied" : "Show Applied"}
          handleButtonPress={() => setShowApplied((prev) => !prev)}
          isButtonDisabled={false}
          solid={!showApplied}
          heightOverride="32px"
          widthOverride="120px"
        />

        <Tooltip title="Refresh" arrow>
          <IconButton
            onClick={fetchExtractions}
            sx={{
              alignSelf: "center",
              color: "#0E4664",
              width: 32,
              height: 32,
              p: 0.5,
              border: "1px solid #cfe0ea",
              backgroundColor: "#f5fbff",
              "& .MuiSvgIcon-root": {
                fontSize: 20,
              },
              "&:hover": {
                backgroundColor: "#e6f2f9",
              },
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && filteredExtractions.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            className="text-general-page-subheader"
          >
            No extractions found
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
            className="text-general-page-text"
          >
            {searchQuery || statusFilter !== "all"
              ? "Try adjusting your filters"
              : "Upload a file to start extracting information"}
          </Typography>
        </Paper>
      )}

      {/* Extractions List */}
      {!loading && filteredExtractions.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {filteredExtractions.map((extraction) => {
            const applied = isExtractionApplied(extraction);
            return (
              <Card
                key={extraction.id}
                id={`extraction-${extraction.id}`}
                sx={{
                  border:
                    highlightId === String(extraction.id)
                      ? "2px solid #1976d2"
                      : applied
                        ? "1px solid #b9c0c8"
                        : "1px solid #e0e0e0",
                  backgroundColor: applied ? "#eceff3" : "background.paper",
                  boxShadow: applied ? "none" : undefined,
                  transition: "all 0.3s ease",
                }}
              >
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          className="text-general-page-subheader"
                          sx={{
                            color: applied ? "text.secondary" : "text.primary",
                            // Override subheader class padding/margin so chip aligns on the same line
                            m: "0 !important",
                            p: "0 !important",
                            lineHeight: "24px !important",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          {extraction.file_name}
                        </Typography>
                        {extraction.deal_id != null ? (
                          <Chip
                            label={`Deal: ${
                              extraction.deal_name || `#${extraction.deal_id}`
                            }`}
                            size="small"
                            clickable
                            onClick={() =>
                              navigate(`/deals/${extraction.deal_id}`)
                            }
                            sx={{
                              flexShrink: 0,
                              height: 24,
                              backgroundColor: "#eef4ff",
                              color: "#224b8f",
                              fontWeight: 500,
                              borderRadius: "999px",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#dfeaff",
                              },
                            }}
                          />
                        ) : (
                          <Chip
                            label="New Deal"
                            size="small"
                            sx={{
                              flexShrink: 0,
                              height: 24,
                              backgroundColor: "#e9f8f1",
                              color: "#176647",
                              fontWeight: 500,
                              borderRadius: "999px",
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}
                      >
                        <Chip
                          {...(getStatusIcon(extraction.status)
                            ? { icon: getStatusIcon(extraction.status)! }
                            : {})}
                          label={
                            extraction.status.charAt(0).toUpperCase() +
                            extraction.status.slice(1)
                          }
                          size="small"
                          sx={{
                            ...getStatusChipSx(extraction.status),
                            borderRadius: "999px",
                            fontWeight: 500,
                            fontSize: "0.76rem",
                            letterSpacing: 0,
                            px: 0.25,
                            "& .MuiChip-icon": {
                              color: "inherit",
                            },
                          }}
                        />
                        {(extraction.is_initial_materials ??
                          extraction.metadata?.is_initial_materials) && (
                          <Chip
                            label="Initial materials"
                            size="small"
                            sx={{
                              backgroundColor: "#efe7ff",
                              color: "#5b36a3",
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {(extraction.is_term_sheet ??
                          extraction.metadata?.is_term_sheet) && (
                          <Chip
                            label="Term sheet"
                            size="small"
                            sx={{
                              backgroundColor: "#e4f2ff",
                              color: "#0d4f8b",
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {extraction.extract_images &&
                          extraction.image_extraction_status && (
                            <Chip
                              label={`Images: ${
                                extraction.image_extraction_status
                                  .charAt(0)
                                  .toUpperCase() +
                                extraction.image_extraction_status.slice(1)
                              }`}
                              size="small"
                              sx={{
                                ...getImageStatusChipSx(
                                  extraction.image_extraction_status,
                                ),
                                fontWeight: 500,
                                borderRadius: "999px",
                              }}
                            />
                          )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          flexWrap: "wrap",
                          mt: 0.75,
                        }}
                      >
                        {extraction.started_at && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="text-general-page-text"
                          >
                            Started:{" "}
                            {formatDistanceToNow(extraction.started_at, {
                              addSuffix: true,
                            })}
                          </Typography>
                        )}
                        {extraction.duration_seconds !== null && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="text-general-page-text"
                          >
                            Duration: {formatDuration(extraction.duration_seconds)}
                          </Typography>
                        )}
                        {extraction.completed_at && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="text-general-page-text"
                          >
                            Completed:{" "}
                            {formatDistanceToNow(extraction.completed_at, {
                              addSuffix: true,
                            })}
                          </Typography>
                        )}
                        {extraction.user_name && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="text-general-page-text"
                          >
                            Created by: {extraction.user_name}
                          </Typography>
                        )}
                      </Box>
                      {extraction.error_message && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {extraction.error_message}
                        </Alert>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        alignSelf: "stretch",
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CustomIconButton
                          tooltip="View extraction"
                          icon={
                            <Box
                              component="img"
                              src={searchIcon}
                              alt="View extraction"
                              sx={{
                                width: 22,
                                height: 22,
                                display: "block",
                                filter:
                                  "invert(21%) sepia(48%) saturate(1166%) hue-rotate(163deg) brightness(92%) contrast(89%)",
                              }}
                            />
                          }
                          onClick={() =>
                            navigate(`/ai-extractions/${extraction.id}`)
                          }
                          iconButtonSx={{ p: 0.6 }}
                        />
                        {canDeleteExtraction(extraction) && (
                          <CustomIconButton
                            tooltip="Delete"
                            icon={
                              <Box
                                component="img"
                                src={binIcon}
                                alt="Delete extraction"
                                sx={{
                                  width: 22,
                                  height: 22,
                                  display: "block",
                                  filter:
                                    "invert(21%) sepia(48%) saturate(1166%) hue-rotate(163deg) brightness(92%) contrast(89%)",
                                }}
                              />
                            }
                            onClick={() => void handleDelete(extraction)}
                            iconButtonSx={{ p: 0.6 }}
                          />
                        )}
                      </Box>
                      {applied && (
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: "999px",
                            backgroundColor: "#5f6b7a",
                            color: "#fff",
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            letterSpacing: 0.1,
                          }}
                        >
                          Applied to deal
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AIExtractions;
